import { Trans, useLingui } from "@lingui/react/macro";
import { setActivityOccurrenceNote } from "@/api/Activities";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import TextArea from "@/components/TextArea/TextArea";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

export const EditActivityOccurrenceNote = ({
  currentNote,
  onSuccess,
  activityId,
  occurrenceId,
}: {
  currentNote: string | null | undefined;
  onSuccess: () => void;
  activityId: string;
  occurrenceId: string;
}) => {
  const { t } = useLingui();
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      note: currentNote ?? "",
    },
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (note: string) =>
      setActivityOccurrenceNote({ activityId, occurrenceId, note }),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSuccess();
    },
  });

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        mutate(formData.note);
      })}
    >
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar kommentar`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : null}
          <Form.Row>
            <TextArea
              label={t`Kommentar`}
              description={t`Visas inte för patienten`}
              showOptionalLabel
              errorMessage={errors.note?.message}
              {...register("note")}
            />
          </Form.Row>
          {isDirty ? (
            <FilledButton type="submit">
              <Trans>Spara ändringar</Trans>
            </FilledButton>
          ) : null}
        </>
      )}
    </Form>
  );
};
